import { useState } from 'react'
import type { FormEvent } from 'react'

import { describeError } from '../lib/api/messages.ts'
import { agents, isAgentId } from '../lib/api/resources.ts'
import { formatClock } from '../lib/format.ts'
import { useAction } from '../lib/useAction.ts'
import { AgentDirectoryTable } from '../components/agents/AgentDirectoryTable.tsx'
import { PageHeader } from '../components/layout/PageHeader.tsx'
import { Badge } from '../components/primitives/Badge.tsx'
import { Button } from '../components/primitives/Button.tsx'
import { CopyValue } from '../components/primitives/CopyValue.tsx'
import { ConfirmDialog } from '../components/primitives/Dialog.tsx'
import { TextField } from '../components/primitives/Field.tsx'
import { TABLE, TD, TH, THEAD, TR } from '../components/primitives/table.ts'
import { useToast } from '../components/primitives/Toast.tsx'

type Operation = 'revoke' | 'suspend' | 'activate'

const OPERATION: Record<
  Operation,
  { label: string; title: string; consequence: string; success: string; destructive: boolean }
> = {
  revoke: {
    label: 'Revogar',
    title: 'Revogar o agente?',
    consequence:
      'Definitivo: esta instalação não volta a autenticar. As sessões ativas caem.',
    success: 'Agente revogado.',
    destructive: true,
  },
  suspend: {
    label: 'Suspender',
    title: 'Suspender o agente?',
    consequence:
      'A coleta para e as sessões ativas caem. Reversível.',
    success: 'Agente suspenso.',
    destructive: true,
  },
  activate: {
    label: 'Reativar',
    title: 'Reativar o agente?',
    consequence:
      'O agente volta a autenticar. As sessões atuais caem.',
    success: 'Agente reativado.',
    destructive: false,
  },
}

interface LogEntry {
  at: string
  agentId: string
  operation: Operation
}

type Tab = 'directory' | 'access'

/**
 * Agentes (Black Ghost).
 *
 * Duas abas. A VISÃO GLOBAL é o diretório de `GET /api/admin/agents`: todos os
 * agentes de todas as empresas, com o estado da varredura de silêncio. O CORTE
 * DE ACESSO é o formulário de revogar, suspender e reativar (`/api/v1/agents`).
 *
 * As duas não se ligam por clique porque falam de identificadores diferentes:
 * o diretório lista por `endpoint_id` (uuid da máquina), e o corte exige o
 * identificador do AGENTE — o SHA-256 da chave pública, que o diretório não traz.
 */
export function Agents() {
  const [tab, setTab] = useState<Tab>('directory')

  return (
    <>
      <PageHeader
        title="Agentes"
        actions={
          <div className="flex items-center gap-1" role="tablist" aria-label="Visão de agentes">
            {(['directory', 'access'] as const).map((value) => (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={tab === value}
                onClick={() => setTab(value)}
                className={`border-b-2 px-2 py-1 text-sm ${
                  tab === value ? 'border-accent text-fg' : 'border-transparent text-fg-muted hover:text-fg'
                }`}
              >
                {value === 'directory' ? 'Visão global' : 'Corte de acesso'}
              </button>
            ))}
          </div>
        }
      />
      {tab === 'directory' ? <AgentDirectoryTable /> : <AccessCut />}
    </>
  )
}

/**
 * O corte de acesso.
 *
 * Esta API **não registra** agente: o registro exige prova de posse da chave
 * privada selada no TPM e a validação do código de instalação, e um endpoint
 * REST que apenas gravasse a chave pública seria um caminho para registrar
 * agente falso.
 *
 * A tela é um formulário: cola-se o identificador do agente — que vem de um
 * alerta ou do inventário do cliente — e escolhe-se a operação. O registro
 * abaixo é local desta sessão, e diz isso: é memória de trabalho para quem está
 * no meio de um incidente, não um histórico do servidor.
 */
function AccessCut() {
  const toast = useToast()
  const action = useAction()

  const [agentId, setAgentId] = useState('')
  const [touched, setTouched] = useState(false)
  const [pending, setPending] = useState<Operation | null>(null)
  const [log, setLog] = useState<LogEntry[]>([])

  const trimmed = agentId.trim()
  const valid = isAgentId(trimmed)
  // O identificador é o SHA-256 da chave pública: 64 hex minúsculos. Validar
  // como GUID — o formato de todo o resto desta API — recusaria o valor certo.
  const localError =
    touched && trimmed !== '' && !valid
      ? 'O identificador do agente é hexadecimal minúsculo de 64 caracteres.'
      : null

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    setTouched(true)
  }

  const run = (operation: Operation): void => {
    action.run(async () => {
      await agents[operation](trimmed)
      toast.success(OPERATION[operation].success)
      setLog((entries) =>
        [{ at: new Date().toISOString(), agentId: trimmed, operation }, ...entries].slice(0, 20),
      )
      setPending(null)
    })
  }

  return (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="max-w-3xl space-y-4 p-3">
          <form onSubmit={submit} className="space-y-3 border border-line bg-surface p-3">
            <TextField
              label="Identificador do agente"
              value={agentId}
              onValueChange={(value) => {
                setAgentId(value)
                setTouched(true)
              }}
              error={localError}
              hint="64 caracteres hexadecimais minúsculos."
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              mono
              placeholder="a3f1…"
            />

            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="danger"
                disabled={!valid || action.busy}
                onClick={() => setPending('revoke')}
              >
                Revogar
              </Button>
              <Button
                variant="danger"
                disabled={!valid || action.busy}
                onClick={() => setPending('suspend')}
              >
                Suspender
              </Button>
              <Button
                variant="default"
                disabled={!valid || action.busy}
                onClick={() => setPending('activate')}
              >
                Reativar
              </Button>
            </div>

            {action.error !== null && (
              <p role="alert" className="border-l-2 border-crit px-2 py-1 text-sm text-crit">
                {describeError(action.error)}
              </p>
            )}
          </form>

          <section>
            <h2 className="mb-1 text-micro font-medium uppercase tracking-label text-fg-secondary">
              Registro desta sessão
            </h2>
            {log.length === 0 ? (
              <p className="text-sm text-fg-muted">Nada ainda.</p>
            ) : (
              <table className={TABLE}>
                <thead className={THEAD}>
                  <tr>
                    <th scope="col" className={TH}>
                      Hora
                    </th>
                    <th scope="col" className={TH}>
                      Operação
                    </th>
                    <th scope="col" className={TH}>
                      Agente
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {log.map((entry) => (
                    <tr key={`${entry.at}-${entry.agentId}`} className={TR}>
                      <td className={`${TD} tnum whitespace-nowrap`}>{formatClock(entry.at)}</td>
                      <td className={TD}>
                        <Badge tone={entry.operation === 'activate' ? 'ok' : 'crit'}>
                          {OPERATION[entry.operation].label}
                        </Badge>
                      </td>
                      <td className={`${TD} max-w-0`}>
                        <CopyValue
                          value={entry.agentId}
                          display={`${entry.agentId.slice(0, 16)}…`}
                          label="identificador do agente"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </div>
      </div>

      {pending !== null && (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open) setPending(null)
          }}
          title={OPERATION[pending].title}
          consequence={
            <span className="flex flex-col gap-2">
              <span className="font-mono text-fg break-all">{trimmed}</span>
              <span>{OPERATION[pending].consequence}</span>
            </span>
          }
          confirmLabel={OPERATION[pending].label}
          destructive={OPERATION[pending].destructive}
          busy={action.busy}
          error={action.error !== null ? describeError(action.error) : null}
          onConfirm={() => run(pending)}
        />
      )}
    </>
  )
}
